import { useState, useEffect } from 'react'
import supabase from '../lib/supabase'

export const useClasses = () => {
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    fetchClasses()
  }, [])

  const fetchClasses = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('classes_quran_portal_9k2m3x')
        .select('*')
        .order('class_number', { ascending: true })

      if (error) {
        console.error('Supabase error:', error)
        throw error
      }

      // Safely parse and validate the data
      const validatedData = (data || []).map(item => ({
        ...item,
        images: Array.isArray(item.images) ? 
          item.images : 
          typeof item.images === 'string' ? 
            (item.images ? JSON.parse(item.images) : []) : 
            []
      }))

      setClasses(validatedData)
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error occurred'
      setError(errorMessage)
      console.error('Error fetching classes:', err)
      // Set empty array on error to prevent app crash
      setClasses([])
    } finally {
      setLoading(false)
    }
  }

  const addClass = async (classData) => {
    try {
      // Validate and prepare data
      const preparedData = {
        ...classData,
        images: Array.isArray(classData.images) ? classData.images : []
      }

      const { data, error } = await supabase
        .from('classes_quran_portal_9k2m3x')
        .insert([preparedData])
        .select()

      if (error) {
        console.error('Insert error:', error)
        throw error
      }

      if (data && data[0]) {
        const newClass = {
          ...data[0],
          images: Array.isArray(data[0].images) ? data[0].images : []
        }
        setClasses(prev => [...prev, newClass])
        return { success: true, data: newClass }
      }

      return { success: false, error: 'No data returned from insert' }
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error occurred'
      console.error('Error adding class:', err)
      return { success: false, error: errorMessage }
    }
  }

  const updateClass = async (classId, updates) => {
    try {
      // Validate and prepare updates
      const preparedUpdates = {
        ...updates,
        images: Array.isArray(updates.images) ? updates.images : [],
        updated_at: new Date().toISOString()
      }

      const { data, error } = await supabase
        .from('classes_quran_portal_9k2m3x')
        .update(preparedUpdates)
        .eq('id', classId)
        .select()

      if (error) {
        console.error('Update error:', error)
        throw error
      }

      if (data && data[0]) {
        const updatedClass = {
          ...data[0],
          images: Array.isArray(data[0].images) ? data[0].images : []
        }
        setClasses(prev => prev.map(cls => cls.id === classId ? updatedClass : cls))
        return { success: true, data: updatedClass }
      }

      return { success: false, error: 'No data returned from update' }
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error occurred'
      console.error('Error updating class:', err)
      return { success: false, error: errorMessage }
    }
  }

  const deleteClass = async (classId) => {
    try {
      const { error } = await supabase
        .from('classes_quran_portal_9k2m3x')
        .delete()
        .eq('id', classId)

      if (error) {
        console.error('Delete error:', error)
        throw error
      }

      setClasses(prev => prev.filter(cls => cls.id !== classId))
      return { success: true }
    } catch (err) {
      const errorMessage = err?.message || 'Unknown error occurred'
      console.error('Error deleting class:', err)
      return { success: false, error: errorMessage }
    }
  }

  return {
    classes,
    loading,
    error,
    fetchClasses,
    addClass,
    updateClass,
    deleteClass
  }
}